// SPDX-License-Identifier: AGPL-3.0-or-later

package one.wabbit.pages

import io.ktor.client.HttpClient
import io.ktor.client.engine.js.Js
import io.ktor.client.plugins.HttpTimeout
import io.ktor.client.plugins.ResponseException
import io.ktor.client.plugins.expectSuccess
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.accept
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.patch
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import kairos.CreateKairosAccountRequest
import kairos.CreateKairosAccountResponse
import kairos.CreateKairosAgentSubaccountRequest
import kairos.CreateKairosAgentSubaccountResponse
import kairos.CreateKairosEventRequest
import kairos.CreateKairosKpiMeasurementRequest
import kairos.CreateKairosKpiRequest
import kairos.CreateKairosObjectiveRequest
import kairos.CreateKairosProjectRequest
import kairos.CreateKairosRoutineRequest
import kairos.CreateKairosSessionRequest
import kairos.CreateKairosSessionResponse
import kairos.CreateKairosTicketBlockerRequest
import kairos.CreateKairosTicketRequest
import kairos.CreateKairosTimeboxRequest
import kairos.CreateKairosWorkspaceMemberRequest
import kairos.KAIROS_WORKSPACE_HEADER
import kairos.Kairos
import kairos.KairosAdminSnapshot
import kairos.KairosAdminUserRow
import kairos.KairosAgentSubaccount
import kairos.KairosAgentWorkspaceGrantRequest
import kairos.KairosErrorResponse
import kairos.KairosSessionInfo
import kairos.KairosWorkspaceMember
import kairos.KairosWorkspaceShareTarget
import kairos.KairosWorkspaceSnapshot
import kairos.PatchKairosAdminUserRequest
import kairos.PatchKairosAgentSubaccountRequest
import kairos.PatchKairosEventRequest
import kairos.PatchKairosKpiRequest
import kairos.PatchKairosObjectiveRequest
import kairos.PatchKairosProjectRequest
import kairos.PatchKairosRoutineRequest
import kairos.PatchKairosTicketRequest
import kairos.PatchKairosTimeboxRequest
import kairos.PatchKairosUserRequest
import kairos.PatchKairosWorkspaceMemberRequest
import kairos.ResolveKairosWorkspaceShareTargetRequest
import kairos.ResolveKairosWorkspaceShareTargetResponse
import kairos.RotateKairosAgentSubaccountTokenResponse
import kairos.ZonedDateTime
import kotlin.coroutines.cancellation.CancellationException
import kotlin.time.Duration
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.Duration.Companion.seconds
import kotlinx.serialization.Serializable
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import one.wabbit.web.common.Etiquette
import one.wabbit.web.common.HttpRetryOptions
import one.wabbit.web.common.Schedule
import one.wabbit.web.common.Timeouts
import one.wabbit.web.common.applyEtiquette
import one.wabbit.web.common.applyTimeouts
import one.wabbit.web.common.httpStrictTransientPolicy
import one.wabbit.web.common.parseRetryAfterHeader
import one.wabbit.web.common.responseBodySampleOrNull
import one.wabbit.web.common.retryingIdempotentHttpCall
import org.w3c.dom.Window

private external val window: Window
private const val LegacyWebAuthTokenStorageKey = "kairos.web.authToken"
private const val WebWorkspaceStorageKey = "kairos.web.workspaceId"

private val siteJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = true
    explicitNulls = false
}

private val http =
    HttpClient(Js) {
        install(HttpTimeout)
        engine { configureRequest { credentials = "include" } }
    }

private val siteApiConfig = KairosSiteApiConfig()

private val readRetryPolicy =
    httpStrictTransientPolicy(
        HttpRetryOptions.strictTransient(
            schedule =
                Schedule.retries(
                    maxRetries = 3,
                    baseDelay = 150.milliseconds,
                    maxDelay = 2.seconds,
                    jitterFactor = 0.2,
                ),
            maxRetryAfterDelay = 2.seconds,
        )
    )

private fun apiBaseUrl(): String {
    val location = window.location
    return if (
        (location.hostname == "localhost" || location.hostname == "127.0.0.1") &&
            location.port != "8081"
    ) {
        "${location.protocol}//${location.hostname}:8081"
    } else {
        location.origin
    }
}

@Serializable
data class WebDownloadsManifest(
    val generatedAt: String,
    val artifacts: List<WebDownloadArtifact> = emptyList(),
)

@Serializable
data class WebDownloadArtifact(
    val id: String,
    val kind: String,
    val title: String,
    val platform: String,
    val architecture: String? = null,
    val version: String? = null,
    val releasedAt: String? = null,
    val filename: String,
    val url: String,
    val description: String,
    val sizeBytes: Long,
    val sha256: String,
)

suspend fun fetchKairosWorkspace(): WebWorkspace = recoverMissingWorkspaceSelection {
    getJson<KairosWorkspaceSnapshot>("/api/workspace").toWebWorkspace()
}

suspend fun fetchKairosDownloadsManifest(): WebDownloadsManifest {
    val url = "${window.location.origin}/resources/downloads/manifest.json"
    val response =
        try {
            retryingIdempotentHttpCall(policy = readRetryPolicy) {
                http.get(url) {
                    expectSuccess = true
                    applyEtiquette(siteApiConfig.etiquette)
                    applyTimeouts(siteApiConfig.timeouts)
                    accept(ContentType.Application.Json)
                }
            }
        } catch (t: Throwable) {
            throw t.toKairosError(method = "GET", url = url)
        }
    return response.decodeJson(method = "GET", url = url)
}

suspend fun fetchKairosSession(): KairosSessionInfo = recoverMissingWorkspaceSelection {
    getJson("/api/session")
}

suspend fun createKairosSession(username: String, password: String): CreateKairosSessionResponse =
    postJson(
        "/api/session/login",
        siteJson.encodeToString(
            CreateKairosSessionRequest.serializer(),
            CreateKairosSessionRequest(username = username, password = password),
        ),
        includeWorkspaceHeader = false,
    )

suspend fun createKairosAccount(
    displayName: String,
    username: String,
    password: String,
    workspaceName: String,
): CreateKairosAccountResponse =
    postJson(
        "/api/session/register",
        siteJson.encodeToString(
            CreateKairosAccountRequest.serializer(),
            CreateKairosAccountRequest(
                displayName = displayName,
                username = username,
                password = password,
                workspaceName = workspaceName,
            ),
        ),
        includeWorkspaceHeader = false,
    )

suspend fun clearKairosSession() {
    postNoContent("/api/session/logout")
}

suspend fun fetchKairosAdminSnapshot(): KairosAdminSnapshot = getJson("/api/admin")

suspend fun updateKairosAdminUserRole(
    userId: Kairos.UserId,
    role: Kairos.UserRole,
): KairosAdminUserRow =
    patchJson(
        "/api/admin/users/${userId.value}",
        siteJson.encodeToString(
            PatchKairosAdminUserRequest.serializer(),
            PatchKairosAdminUserRequest(role = role),
        ),
    )

suspend fun fetchKairosAgentSubaccounts(): List<KairosAgentSubaccount> =
    getJson("/api/account/agents")

suspend fun createKairosAgentSubaccount(
    displayName: String,
    username: String,
    workspaceAccess: List<KairosAgentWorkspaceGrantRequest>,
): CreateKairosAgentSubaccountResponse =
    postJson(
        "/api/account/agents",
        siteJson.encodeToString(
            CreateKairosAgentSubaccountRequest.serializer(),
            CreateKairosAgentSubaccountRequest(
                displayName = displayName,
                username = username,
                workspaceAccess = workspaceAccess,
            ),
        ),
    )

suspend fun updateKairosAgentSubaccount(
    userId: Kairos.UserId,
    displayName: String,
): KairosAgentSubaccount =
    patchJson(
        "/api/account/agents/${userId.value}",
        siteJson.encodeToString(
            PatchKairosAgentSubaccountRequest.serializer(),
            PatchKairosAgentSubaccountRequest(displayName = displayName),
        ),
    )

suspend fun rotateKairosAgentSubaccountToken(
    userId: Kairos.UserId
): RotateKairosAgentSubaccountTokenResponse =
    postJson("/api/account/agents/${userId.value}/rotate-token", "{}")

suspend fun upsertKairosAgentWorkspaceAccess(
    userId: Kairos.UserId,
    workspaceId: Kairos.WorkspaceId,
    role: Kairos.WorkspaceMembershipRole,
): KairosAgentSubaccount =
    postJson(
        "/api/account/agents/${userId.value}/workspaces",
        siteJson.encodeToString(
            KairosAgentWorkspaceGrantRequest.serializer(),
            KairosAgentWorkspaceGrantRequest(workspaceId = workspaceId, role = role),
        ),
    )

suspend fun removeKairosAgentWorkspaceAccess(
    userId: Kairos.UserId,
    workspaceId: Kairos.WorkspaceId,
): KairosAgentSubaccount =
    deleteJson("/api/account/agents/${userId.value}/workspaces/${workspaceId.value}")

suspend fun removeKairosAgentSubaccount(userId: Kairos.UserId) {
    deleteJsonNoContent("/api/account/agents/${userId.value}")
}

suspend fun updateKairosUser(userId: Kairos.UserId, request: PatchKairosUserRequest): Kairos.User =
    patchJson(
        "/api/users/${userId.value}",
        siteJson.encodeToString(PatchKairosUserRequest.serializer(), request),
    )

suspend fun fetchKairosWorkspaceMembers(): List<KairosWorkspaceMember> =
    getJson("/api/workspace/members")

suspend fun resolveKairosWorkspaceShareTarget(identifier: String): KairosWorkspaceShareTarget? =
    postJson<ResolveKairosWorkspaceShareTargetResponse>(
            "/api/workspace/share-target",
            siteJson.encodeToString(
                ResolveKairosWorkspaceShareTargetRequest.serializer(),
                ResolveKairosWorkspaceShareTargetRequest(identifier = identifier),
            ),
        )
        .target

suspend fun createKairosWorkspaceMember(
    username: String,
    role: Kairos.WorkspaceMembershipRole = Kairos.WorkspaceMembershipRole.Member,
): List<KairosWorkspaceMember> =
    postJson(
        "/api/workspace/members",
        siteJson.encodeToString(
            CreateKairosWorkspaceMemberRequest.serializer(),
            CreateKairosWorkspaceMemberRequest(username = username, role = role),
        ),
    )

suspend fun updateKairosWorkspaceMemberRole(
    userId: Kairos.UserId,
    role: Kairos.WorkspaceMembershipRole,
): List<KairosWorkspaceMember> =
    patchJson(
        "/api/workspace/members/${userId.value}",
        siteJson.encodeToString(
            PatchKairosWorkspaceMemberRequest.serializer(),
            PatchKairosWorkspaceMemberRequest(role = role),
        ),
    )

suspend fun removeKairosWorkspaceMember(userId: Kairos.UserId): List<KairosWorkspaceMember> =
    deleteJson("/api/workspace/members/${userId.value}")

suspend fun createKairosProject(name: String, parentProjectId: Kairos.ProjectId?): Kairos.Project =
    postJson(
        "/api/projects",
        siteJson.encodeToString(
            CreateKairosProjectRequest.serializer(),
            CreateKairosProjectRequest(name = name, parentProjectId = parentProjectId),
        ),
    )

suspend fun updateKairosProject(
    projectId: Kairos.ProjectId,
    name: String?,
    parentProjectId: Kairos.ProjectId?,
    clearParent: Boolean,
): Kairos.Project =
    patchJson(
        "/api/projects/${projectId.value}",
        siteJson.encodeToString(
            PatchKairosProjectRequest.serializer(),
            PatchKairosProjectRequest(
                name = name,
                parentProjectId = parentProjectId,
                clearParent = clearParent,
            ),
        ),
    )

suspend fun createKairosTicket(
    projectId: Kairos.ProjectId,
    title: String,
    assigneeUserId: Kairos.UserId? = null,
): Kairos.Ticket =
    postJson(
        "/api/tickets",
        siteJson.encodeToString(
            CreateKairosTicketRequest.serializer(),
            CreateKairosTicketRequest(
                projectId = projectId,
                title = title,
                assigneeUserId = assigneeUserId,
            ),
        ),
    )

suspend fun setKairosTicketCompleted(ticketId: Kairos.TicketId, completed: Boolean): Kairos.Ticket =
    patchJson(
        "/api/tickets/${ticketId.value}",
        siteJson.encodeToString(
            PatchKairosTicketRequest.serializer(),
            PatchKairosTicketRequest(completed = completed),
        ),
    )

suspend fun updateKairosTicketTitle(ticketId: Kairos.TicketId, title: String): Kairos.Ticket =
    patchJson(
        "/api/tickets/${ticketId.value}",
        siteJson.encodeToString(
            PatchKairosTicketRequest.serializer(),
            PatchKairosTicketRequest(title = title),
        ),
    )

suspend fun updateKairosTicketDescription(
    ticketId: Kairos.TicketId,
    description: String,
): Kairos.Ticket =
    patchJson(
        "/api/tickets/${ticketId.value}",
        siteJson.encodeToString(
            PatchKairosTicketRequest.serializer(),
            PatchKairosTicketRequest(description = description),
        ),
    )

suspend fun updateKairosTicketStage(
    ticketId: Kairos.TicketId,
    stage: Kairos.TicketStage,
): Kairos.Ticket =
    patchJson(
        "/api/tickets/${ticketId.value}",
        siteJson.encodeToString(
            PatchKairosTicketRequest.serializer(),
            PatchKairosTicketRequest(stage = stage),
        ),
    )

suspend fun updateKairosTicketAssignee(
    ticketId: Kairos.TicketId,
    assigneeUserId: Kairos.UserId?,
): Kairos.Ticket =
    patchJson(
        "/api/tickets/${ticketId.value}",
        siteJson.encodeToString(
            PatchKairosTicketRequest.serializer(),
            PatchKairosTicketRequest(
                assigneeUserId = assigneeUserId,
                clearAssignee = assigneeUserId == null,
            ),
        ),
    )

suspend fun updateKairosTicketProject(
    ticketId: Kairos.TicketId,
    projectId: Kairos.ProjectId,
): Kairos.Ticket =
    patchJson(
        "/api/tickets/${ticketId.value}",
        siteJson.encodeToString(
            PatchKairosTicketRequest.serializer(),
            PatchKairosTicketRequest(projectId = projectId),
        ),
    )

suspend fun updateKairosTicketPriority(
    ticketId: Kairos.TicketId,
    priority: Kairos.TicketPriority,
): Kairos.Ticket =
    patchJson(
        "/api/tickets/${ticketId.value}",
        siteJson.encodeToString(
            PatchKairosTicketRequest.serializer(),
            PatchKairosTicketRequest(priority = priority),
        ),
    )

suspend fun updateKairosTicketUrgency(
    ticketId: Kairos.TicketId,
    urgency: Kairos.TicketUrgency,
): Kairos.Ticket =
    patchJson(
        "/api/tickets/${ticketId.value}",
        siteJson.encodeToString(
            PatchKairosTicketRequest.serializer(),
            PatchKairosTicketRequest(urgency = urgency),
        ),
    )

suspend fun updateKairosTicketDeadline(
    ticketId: Kairos.TicketId,
    deadline: ZonedDateTime?,
): Kairos.Ticket =
    patchJson(
        "/api/tickets/${ticketId.value}",
        siteJson.encodeToString(
            PatchKairosTicketRequest.serializer(),
            PatchKairosTicketRequest(deadline = deadline, clearDeadline = deadline == null),
        ),
    )

suspend fun addKairosTicketBlocker(
    blockedTicketId: Kairos.TicketId,
    blockingTicketId: Kairos.TicketId,
): Kairos.TicketBlocker =
    postJson(
        "/api/tickets/${blockedTicketId.value}/blockers",
        siteJson.encodeToString(
            CreateKairosTicketBlockerRequest.serializer(),
            CreateKairosTicketBlockerRequest(blockingTicketId),
        ),
    )

suspend fun removeKairosTicketBlocker(
    blockedTicketId: Kairos.TicketId,
    blockingTicketId: Kairos.TicketId,
) {
    deleteJsonNoContent("/api/tickets/${blockedTicketId.value}/blockers/${blockingTicketId.value}")
}

suspend fun createKairosObjective(
    description: String,
    projectId: Kairos.ProjectId?,
    deadline: String? = null,
    kpiTargets: List<Kairos.KpiWithTarget> = emptyList(),
    tickets: List<Kairos.TicketId> = emptyList(),
): Kairos.Objective =
    postJson(
        "/api/objectives",
        siteJson.encodeToString(
            CreateKairosObjectiveRequest.serializer(),
            CreateKairosObjectiveRequest(
                projectId = projectId,
                description = description,
                deadline = deadline,
                kpiTargets = kpiTargets,
                tickets = tickets,
            ),
        ),
    )

suspend fun updateKairosObjectiveTickets(
    objectiveId: Kairos.ObjectiveId,
    tickets: List<Kairos.TicketId>,
): Kairos.Objective =
    patchJson(
        "/api/objectives/${objectiveId.value}",
        siteJson.encodeToString(
            PatchKairosObjectiveRequest.serializer(),
            PatchKairosObjectiveRequest(tickets = tickets),
        ),
    )

suspend fun updateKairosObjectiveKpiTargets(
    objectiveId: Kairos.ObjectiveId,
    kpiTargets: List<Kairos.KpiWithTarget>,
): Kairos.Objective =
    patchJson(
        "/api/objectives/${objectiveId.value}",
        siteJson.encodeToString(
            PatchKairosObjectiveRequest.serializer(),
            PatchKairosObjectiveRequest(kpiTargets = kpiTargets),
        ),
    )

suspend fun updateKairosObjective(
    objectiveId: Kairos.ObjectiveId,
    description: String,
    deadline: String? = null,
): Kairos.Objective =
    patchJson(
        "/api/objectives/${objectiveId.value}",
        siteJson.encodeToString(
            PatchKairosObjectiveRequest.serializer(),
            PatchKairosObjectiveRequest(description = description, deadline = deadline),
        ),
    )

suspend fun createKairosKpi(
    projectId: Kairos.ProjectId?,
    name: String,
    targetValue: Kairos.KpiTarget?,
    origin: Kairos.KpiOrigin? = null,
): Kairos.Kpi =
    postJson(
        "/api/kpis",
        siteJson.encodeToString(
            CreateKairosKpiRequest.serializer(),
            CreateKairosKpiRequest(
                projectId = projectId,
                name = name,
                targetValue = targetValue,
                origin = origin,
            ),
        ),
    )

suspend fun updateKairosKpi(
    kpiId: Kairos.KpiId,
    name: String?,
    targetValue: Kairos.KpiTarget?,
    clearTarget: Boolean,
    origin: Kairos.KpiOrigin?,
): Kairos.Kpi =
    patchJson(
        "/api/kpis/${kpiId.value}",
        siteJson.encodeToString(
            PatchKairosKpiRequest.serializer(),
            PatchKairosKpiRequest(
                name = name,
                targetValue = targetValue,
                clearTarget = clearTarget,
                origin = origin,
            ),
        ),
    )

suspend fun createKairosKpiMeasurement(kpiId: Kairos.KpiId, value: Double): Kairos.KpiMeasurement =
    postJson(
        "/api/kpis/${kpiId.value}/measurements",
        siteJson.encodeToString(
            CreateKairosKpiMeasurementRequest.serializer(),
            CreateKairosKpiMeasurementRequest(value = value),
        ),
    )

suspend fun createKairosEvent(
    projectId: Kairos.ProjectId?,
    title: String,
    summary: String,
    dtStart: ZonedDateTime,
    dtEnd: ZonedDateTime?,
    description: String?,
    location: String?,
): Kairos.Event =
    postJson(
        "/api/events",
        siteJson.encodeToString(
            CreateKairosEventRequest.serializer(),
            CreateKairosEventRequest(
                projectId = projectId,
                title = title,
                summary = summary,
                dtStart = dtStart,
                dtEnd = dtEnd,
                description = description,
                location = location,
            ),
        ),
    )

suspend fun updateKairosEvent(
    eventId: Kairos.EventId,
    title: String,
    summary: String,
    dtStart: ZonedDateTime,
    dtEnd: ZonedDateTime?,
): Kairos.Event =
    patchJson(
        "/api/events/${eventId.value}",
        siteJson.encodeToString(
            PatchKairosEventRequest.serializer(),
            PatchKairosEventRequest(
                title = title,
                summary = summary,
                dtStart = dtStart,
                dtEnd = dtEnd,
                clearDtEnd = dtEnd == null,
            ),
        ),
    )

suspend fun createKairosTimebox(
    projectId: Kairos.ProjectId?,
    tickets: List<Kairos.TicketId>,
    description: String,
    startTime: ZonedDateTime,
    durationHours: Int?,
): Kairos.Timebox =
    postJson(
        "/api/timeboxes",
        siteJson.encodeToString(
            CreateKairosTimeboxRequest.serializer(),
            CreateKairosTimeboxRequest(
                projectId = projectId,
                tickets = tickets,
                description = description,
                startTime = startTime,
                durationHours = durationHours,
            ),
        ),
    )

suspend fun updateKairosTimebox(
    timeboxId: Kairos.TimeboxId,
    description: String,
    startTime: ZonedDateTime,
    durationHours: Int?,
): Kairos.Timebox =
    patchJson(
        "/api/timeboxes/${timeboxId.value}",
        siteJson.encodeToString(
            PatchKairosTimeboxRequest.serializer(),
            PatchKairosTimeboxRequest(
                description = description,
                startTime = startTime,
                durationHours = durationHours,
                clearRecordedDuration = durationHours == null,
            ),
        ),
    )

suspend fun createKairosRoutine(projectId: Kairos.ProjectId?, description: String): Kairos.Routine =
    postJson(
        "/api/routines",
        siteJson.encodeToString(
            CreateKairosRoutineRequest.serializer(),
            CreateKairosRoutineRequest(projectId = projectId, description = description),
        ),
    )

suspend fun updateKairosRoutine(routineId: Kairos.RoutineId, description: String): Kairos.Routine =
    patchJson(
        "/api/routines/${routineId.value}",
        siteJson.encodeToString(
            PatchKairosRoutineRequest.serializer(),
            PatchKairosRoutineRequest(description = description),
        ),
    )

private suspend inline fun <reified T> getJson(path: String): T {
    val url = "${apiBaseUrl()}$path"
    val response =
        try {
            retryingIdempotentHttpCall(policy = readRetryPolicy) {
                http.get(url) { applySiteRequestDefaults() }
            }
        } catch (t: Throwable) {
            throw t.toKairosError(method = "GET", url = url)
        }
    return response.decodeJson(method = "GET", url = url)
}

private suspend inline fun <reified T> postJson(
    path: String,
    body: String,
    includeWorkspaceHeader: Boolean = true,
): T =
    writeJson(path = path, method = "POST") {
        post(it) { applyJsonBody(body, includeWorkspaceHeader = includeWorkspaceHeader) }
    }

private suspend inline fun <reified T> patchJson(path: String, body: String): T =
    writeJson(path = path, method = "PATCH") { patch(it) { applyJsonBody(body) } }

private suspend inline fun <reified T> deleteJson(path: String): T {
    val url = "${apiBaseUrl()}$path"
    val response =
        try {
            http.delete(url) { applySiteRequestDefaults() }
        } catch (t: Throwable) {
            throw t.toKairosError(method = "DELETE", url = url)
        }
    return response.decodeJson(method = "DELETE", url = url)
}

private suspend fun deleteJsonNoContent(path: String) {
    val url = "${apiBaseUrl()}$path"
    try {
        http.delete(url) { applySiteRequestDefaults() }
    } catch (t: Throwable) {
        throw t.toKairosError(method = "DELETE", url = url)
    }
}

private suspend fun postNoContent(path: String) {
    val url = "${apiBaseUrl()}$path"
    try {
        http.post(url) { applyJsonBody("{}") }
    } catch (t: Throwable) {
        throw t.toKairosError(method = "POST", url = url)
    }
}

private suspend inline fun <reified T> writeJson(
    path: String,
    method: String,
    crossinline execute: suspend HttpClient.(String) -> HttpResponse,
): T {
    val url = "${apiBaseUrl()}$path"
    val response =
        try {
            http.execute(url)
        } catch (t: Throwable) {
            throw t.toKairosError(method = method, url = url)
        }
    return response.decodeJson(method = method, url = url)
}

private fun KairosWorkspaceSnapshot.toWebWorkspace(): WebWorkspace =
    WebWorkspace(
        workspace = workspace,
        memberships = memberships,
        sourceName = sourceName,
        users = users,
        projects = projects,
        tickets = tickets,
        ticketBlockers = ticketBlockers,
        activities = activities,
        objectives = objectives,
        kpis = kpis,
        measurements = measurements,
        kpiEvaluations = kpiEvaluations,
        timeboxes = timeboxes,
        routines = routines,
        events = events,
    )

private data class KairosSiteApiConfig(
    val etiquette: Etiquette = Etiquette("one.wabbit.kairos.web/0.1"),
    val timeouts: Timeouts = Timeouts(request = 8.seconds, connect = 4.seconds, socket = 8.seconds),
)

private sealed class KairosSiteApiError(message: String, cause: Throwable? = null) :
    RuntimeException(message, cause) {
    class Http(
        method: String,
        url: String,
        val status: Int,
        val retryAfter: Duration?,
        val bodySample: String?,
    ) :
        KairosSiteApiError(
            buildString {
                append(method)
                append(" ")
                append(url)
                append(" returned HTTP ")
                append(status)
                retryAfter?.let {
                    append(" (Retry-After ")
                    append(it)
                    append(")")
                }
                bodySample
                    ?.takeIf { it.isNotBlank() }
                    ?.let {
                        append(". Body: ")
                        append(it.squashForMessage())
                    }
            }
        )

    class Network(method: String, url: String, cause: Throwable) :
        KairosSiteApiError("$method $url failed before a response was received", cause)

    class Parse(method: String, url: String, bodySample: String?, cause: Throwable) :
        KairosSiteApiError(
            buildString {
                append("Failed to parse ")
                append(method)
                append(" ")
                append(url)
                bodySample
                    ?.takeIf { it.isNotBlank() }
                    ?.let {
                        append(". Body: ")
                        append(it.squashForMessage())
                    }
            },
            cause,
        )
}

private fun HttpRequestBuilder.applySiteRequestDefaults(includeWorkspaceHeader: Boolean = true) {
    expectSuccess = true
    applyEtiquette(siteApiConfig.etiquette)
    applyTimeouts(siteApiConfig.timeouts)
    accept(ContentType.Application.Json)
    if (includeWorkspaceHeader) {
        storedWorkspaceId()?.let { workspaceId ->
            headers.append(KAIROS_WORKSPACE_HEADER, workspaceId)
        }
    }
}

private fun HttpRequestBuilder.applyJsonBody(body: String, includeWorkspaceHeader: Boolean = true) {
    applySiteRequestDefaults(includeWorkspaceHeader = includeWorkspaceHeader)
    contentType(ContentType.Application.Json)
    setBody(body)
}

private suspend inline fun <reified T> HttpResponse.decodeJson(method: String, url: String): T {
    val text =
        try {
            bodyAsText()
        } catch (t: Throwable) {
            if (t is CancellationException) throw t
            throw KairosSiteApiError.Network(method = method, url = url, cause = t)
        }
    return try {
        siteJson.decodeFromString<T>(text)
    } catch (t: Throwable) {
        if (t is CancellationException) throw t
        throw KairosSiteApiError.Parse(
            method = method,
            url = url,
            bodySample = text.take(MAX_BODY_SAMPLE_CHARS),
            cause = t,
        )
    }
}

private suspend fun Throwable.toKairosError(method: String, url: String): KairosSiteApiError {
    if (this is CancellationException) throw this
    return when (this) {
        is KairosSiteApiError -> this
        is ResponseException ->
            KairosSiteApiError.Http(
                method = method,
                url = url,
                status = response.status.value,
                retryAfter = parseRetryAfterHeader(response.headers[HttpHeaders.RetryAfter]),
                bodySample = responseBodySampleOrNull(MAX_BODY_SAMPLE_CHARS),
            )
        else -> KairosSiteApiError.Network(method = method, url = url, cause = this)
    }
}

private fun String.squashForMessage(): String =
    replace('\n', ' ').replace('\r', ' ').replace('\t', ' ').trim().take(MAX_MESSAGE_SAMPLE_CHARS)

internal fun storedWorkspaceId(): String? =
    try {
        window.localStorage.getItem(WebWorkspaceStorageKey)?.trim()?.takeIf { it.isNotEmpty() }
    } catch (_: Throwable) {
        null
    }

internal fun storeAuthToken(token: String?) {
    try {
        token
        window.localStorage.removeItem(LegacyWebAuthTokenStorageKey)
    } catch (_: Throwable) {
        // Ignore storage failures and continue without a persisted auth token.
    }
}

internal fun storeWorkspaceId(workspaceId: String?) {
    try {
        if (workspaceId == null) {
            window.localStorage.removeItem(WebWorkspaceStorageKey)
        } else {
            window.localStorage.setItem(WebWorkspaceStorageKey, workspaceId)
        }
    } catch (_: Throwable) {
        // Ignore storage failures and continue without a persisted workspace selector.
    }
}

private suspend fun <T> recoverMissingWorkspaceSelection(block: suspend () -> T): T {
    try {
        return block()
    } catch (error: KairosSiteApiError.Http) {
        if ((error.status == 400 || error.status == 404) && storedWorkspaceId() != null) {
            storeWorkspaceId(null)
            return block()
        }
        throw error
    }
}

internal fun Throwable.isUnauthorizedKairosApiError(): Boolean =
    this is KairosSiteApiError.Http && status == 401

internal fun Throwable.reachedKairosApi(): Boolean =
    this is KairosSiteApiError.Http || this is KairosSiteApiError.Parse

internal fun Throwable.kairosApiMessageOrNull(): String? {
    val httpError = this as? KairosSiteApiError.Http ?: return null
    val rawBody = httpError.bodySample?.trim()?.takeIf { it.isNotEmpty() } ?: return null
    return try {
        siteJson.decodeFromString<KairosErrorResponse>(rawBody).message
    } catch (_: Throwable) {
        null
    }
}

private const val MAX_BODY_SAMPLE_CHARS = 2048
private const val MAX_MESSAGE_SAMPLE_CHARS = 256
